/**
 * AssigneeMenu (INBOX-PODIUM-FEEL slice 3) — Podium-style "assign this
 * conversation" control. Shows WHO owns the thread (initials avatar + name) and
 * lets you assign it to anyone on the team, claim it for yourself, or unassign.
 * The assignee is an EMAIL that matches Podium's assignee_email, so the write-
 * back in useUpdateConversation keeps the two inboxes in agreement.
 *
 * Avatars are initials on a stable per-person color (no photo dependency — the
 * office staff aren't all in the tech-photos bucket).
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, UserPlus, Check, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { TOOL_BTN } from "./toolbar";
import { supabase } from "@/lib/supabase";

export interface AssignUser {
  email: string;
  name: string;
  first: string;
}

const AV_COLORS = [
  "bg-rose-500", "bg-orange-500", "bg-amber-500", "bg-emerald-500", "bg-teal-500",
  "bg-sky-500", "bg-indigo-500", "bg-violet-500", "bg-fuchsia-500", "bg-pink-500",
];
function colorFor(s: string) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
}
function initials(name: string) {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || "?";
}

export function Avatar({ name, email, size = "sm" }: { name: string; email: string; size?: "sm" | "xs" }) {
  const cls = size === "xs" ? "h-5 w-5 text-[9px]" : "h-6 w-6 text-[10px]";
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white",
        cls,
        colorFor(email),
      )}
      title={name}
    >
      {initials(name)}
    </span>
  );
}

/** Active Sedgwick staff from the mirrored Podium users (drop Podium bots). */
export function useAssignableUsers() {
  return useQuery({
    queryKey: ["assignable_users"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<AssignUser[]> => {
      const { data, error } = await supabase
        .from("podium_user")
        .select("email, first_name, last_name, role, archived")
        .eq("archived", false);
      if (error) throw error;
      return (data ?? [])
        .filter((u) => u.email && !/@podium\.com$/i.test(u.email as string) && u.role !== "mock_support")
        .map((u) => ({
          email: u.email as string,
          first: ((u.first_name as string) || (u.email as string)).trim(),
          name: `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || (u.email as string),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

/** Resolve an assignee email to a friendly first name (for list chips). */
export function useAssigneeName(email: string | null): string | null {
  const { data } = useAssignableUsers();
  if (!email) return null;
  return data?.find((u) => u.email === email)?.first ?? email.split("@")[0];
}

export function AssigneeMenu({
  assignee,
  onAssign,
  myEmail,
}: {
  assignee: string | null;
  onAssign: (email: string | null) => void;
  myEmail: string | null;
}) {
  const { data: users } = useAssignableUsers();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = users?.find((u) => u.email === assignee);
  const currentName = current?.name ?? (assignee ? assignee.split("@")[0] : null);
  const meName = users?.find((u) => u.email === myEmail)?.name ?? myEmail ?? "";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={assignee ? `Assigned to ${currentName}` : "Assign this conversation"}
        className={cn(TOOL_BTN, "pl-2")}
      >
        {assignee ? (
          <Avatar name={currentName || assignee} email={assignee} size="xs" />
        ) : (
          <UserPlus className="h-3.5 w-3.5" />
        )}
        <span className="max-w-[130px] truncate">{currentName ?? "Assign"}</span>
        <ChevronDown className="h-3 w-3 text-slate-400" />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1.5 w-56 overflow-hidden rounded-xl border border-hairline bg-white py-1 shadow-e3">
          {myEmail && assignee !== myEmail && (
            <button
              type="button"
              onClick={() => {
                onAssign(myEmail);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] font-semibold text-brand-700 hover:bg-brand-50"
            >
              <Avatar name={meName} email={myEmail} size="xs" /> Assign to me
            </button>
          )}
          <div className="max-h-64 overflow-y-auto">
            {(users ?? []).map((u) => (
              <button
                key={u.email}
                type="button"
                onClick={() => {
                  onAssign(u.email);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-slate-700 hover:bg-slate-50"
              >
                <Avatar name={u.name} email={u.email} size="xs" />
                <span className="min-w-0 flex-1 truncate">
                  {u.name}
                  {u.email === myEmail ? " (you)" : ""}
                </span>
                {assignee === u.email && <Check className="h-3.5 w-3.5 text-brand-600" />}
              </button>
            ))}
          </div>
          {assignee && (
            <>
              <div className="my-1 border-t border-hairline" />
              <button
                type="button"
                onClick={() => {
                  onAssign(null);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] font-medium text-slate-500 hover:bg-slate-50"
              >
                <X className="h-3.5 w-3.5" /> Unassign
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
